import React, { useState } from 'react';
import axios from 'axios';
import { ENDPOINT } from '../api/auth';
import { FaChevronDown, FaChevronUp, FaCalendarAlt, FaChartLine, FaMoneyBillWave, FaExchangeAlt } from 'react-icons/fa';
import './reports.css';

// Collapsible Section Component
const CollapsibleSection = ({ title, data, renderItem, count, defaultCollapsed = true }) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <div className="collapsible-section">
      <div className="section-header" onClick={() => setCollapsed(!collapsed)}>
        <h3 className="section-title">{`${title} (${count})`}</h3>
        {collapsed ? <FaChevronDown /> : <FaChevronUp />}
      </div>
      {!collapsed && (
        <div className="section-content">
          {data.length > 0 ? (
            data.map(renderItem)
          ) : (
            <div className="no-data">No data for this period.</div>
          )}
        </div>
      )}
    </div>
  );
};

const Reports = () => {
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [details, setDetails] = useState(null);

  const fetchReport = async () => {
    setLoading(true);
    setReport(null);
    setDetails(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${ENDPOINT}?action=getAnalyticsAndReports&start_date=${startDate}&end_date=${endDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setReport(response.data.summary);
        setDetails({
          sales: response.data.sales || [],
          expenses: response.data.expenses || [],
          deposits: response.data.deposits || [],
          payment_records: response.data.payment_records || []
        });
      } else {
        alert('Error: ' + (response.data.message || 'Failed to fetch report'));
      }
    } catch (error) {
      console.error('Report fetch error:', error);
      alert('Error: Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };


  return (
    <div className="reports-container">
      {/* Header Card */}
      <div className="reports-header-card">
        <div className="header-content">
          <div className="header-title">
            <FaChartLine className="title-icon" />
            <h1>Sales Analytics Report</h1>
          </div>
          <div className="date-controls">
            <div className="date-input-group">
              <FaCalendarAlt className="date-icon" />
              <label>From:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="date-input"
              />
            </div>
            <div className="date-input-group">
              <FaCalendarAlt className="date-icon" />
              <label>To:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="date-input"
              />
            </div>
            <button
              onClick={fetchReport}
              disabled={loading}
              className="fetch-button"
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Loading...
                </>
              ) : (
                'Generate Report'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="spinner large"></div>
          <p>Generating comprehensive report...</p>
        </div>
      )}

      {/* Summary Cards */}
      {report && (
        <div className="summary-section">
          <div className="summary-grid">
            <div className="metric-card sales">
              <div className="metric-icon">
                <FaMoneyBillWave />
              </div>
              <div className="metric-content">
                <div className="metric-label">Total Sales</div>
                <div className="metric-value">{formatCurrency(report.total_sales)}</div>
              </div>
            </div>
            
            <div className="metric-card profit">
              <div className="metric-icon">
                <FaChartLine />
              </div>
              <div className="metric-content">
                <div className="metric-label">Total Profit</div>
                <div className="metric-value">{formatCurrency(report.total_profit)}</div>
              </div>
            </div>
            
            <div className="metric-card expenses">
              <div className="metric-icon">
                <FaExchangeAlt />
              </div>
              <div className="metric-content">
                <div className="metric-label">Total Expenses</div>
                <div className="metric-value">{formatCurrency(report.total_expenses)}</div>
              </div>
            </div>
            
            <div className="metric-card net-income">
              <div className="metric-icon">
                <FaChartLine />
              </div>
              <div className="metric-content">
                <div className="metric-label">Net Income</div>
                <div className="metric-value">{formatCurrency(report.net_income)}</div>
              </div>
            </div>
            
            <div className="metric-card credit">
              <div className="metric-icon">
                <FaExchangeAlt />
              </div>
              <div className="metric-content">
                <div className="metric-label">Total Credit</div>
                <div className="metric-value">{formatCurrency(report.total_credit)}</div>
              </div>
            </div>
            
            <div className="metric-card cash">
              <div className="metric-icon">
                <FaMoneyBillWave />
              </div>
              <div className="metric-content">
                <div className="metric-label">Cash Balance</div>
                <div className="metric-value">{formatCurrency(report.cash_balance)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Sections */}
      {details && (
        <div className="details-section">
          <CollapsibleSection
            title="Sales Transactions"
            count={details.sales.length}
            data={details.sales}
            renderItem={(item, index) => (
              <div key={`sale-${index}`} className="detail-item">
                <div className="item-main">
                  <div className="item-title">
                    {item.product_name || 'Product'} - Qty: {item.quantity}
                  </div>
                  <div className="item-subtitle">
                    Date: {new Date(item.transaction_date).toLocaleDateString()} | 
                    Method: {item.payment_method || 'N/A'}
                  </div>
                </div>
                <div className="item-amount">
                  {formatCurrency(item.item_total || (item.quantity * item.unit_price))}
                </div>
              </div>
            )}
          />
          
          <CollapsibleSection
            title="Expenses"
            count={details.expenses.length}
            data={details.expenses}
            renderItem={(item, index) => (
              <div key={`expense-${index}`} className="detail-item">
                <div className="item-main">
                  <div className="item-title">
                    {item.reason || 'Expense'}
                  </div>
                  <div className="item-subtitle">
                    Date: {new Date(item.transaction_date).toLocaleDateString()} | 
                    Category: {item.category || 'General'}
                  </div>
                </div>
                <div className="item-amount expense">
                  -{formatCurrency(item.amount)}
                </div>
              </div>
            )}
          />
          
          <CollapsibleSection
            title="Bank Deposits"
            count={details.deposits.length}
            data={details.deposits}
            renderItem={(item, index) => (
              <div key={`deposit-${index}`} className="detail-item">
                <div className="item-main">
                  <div className="item-title">
                    Deposit to {item.bank_name}
                  </div>
                  <div className="item-subtitle">
                    Date: {new Date(item.deposit_date).toLocaleDateString()}
                  </div>
                </div>
                <div className="item-amount deposit">
                  +{formatCurrency(item.amount)}
                </div>
              </div>
            )}
          />
          
          <CollapsibleSection
            title="Payment Records"
            count={details.payment_records.length}
            data={details.payment_records}
            renderItem={(item, index) => (
              <div key={`payment-${index}`} className="detail-item">
                <div className="item-main">
                  <div className="item-title">
                    Payment from {item.customer_name || 'Customer'}
                  </div>
                  <div className="item-subtitle">
                    Date: {new Date(item.created_at).toLocaleDateString()} | 
                    Method: {item.payment_method} {item.bank_name ? `(${item.bank_name})` : ''}
                  </div>
                </div>
                <div className="item-amount payment">
                  +{formatCurrency(item.payment_amount)}
                </div>
              </div>
            )}
          />
        </div>
      )}
    </div>
  );
};

export default Reports;
